/**
 * UserMenu Component
 * User dropdown menu with profile, settings, and logout options
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { User, Crown, Zap, Home, LogOut } from 'lucide-react';

export const UserMenu = ({
  user,
  planId,
  logout,
}) => {
  const navigate = useNavigate();
  
  return (
    <>
      <div className="w-px h-6 bg-zinc-800 ml-2 hidden md:block" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-2 px-2 ml-1"
            data-testid="daw-user-menu"
          >
            <div className="w-7 h-7 rounded-full bg-indigo-500/20 flex items-center justify-center">
              <User className="w-4 h-4 text-indigo-400" />
            </div>
            <span className="hidden lg:block text-sm text-zinc-400">{user?.name?.split(' ')[0]}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 glass border-white/10">
          <div className="px-3 py-2">
            <p className="font-medium text-sm">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            <div className="mt-1">
              {planId === 'studio' ? (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400">
                  <Crown className="w-3 h-3" /> Studio
                </span>
              ) : planId === 'pro' ? (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-400">
                  <Zap className="w-3 h-3" /> Pro
                </span>
              ) : (
                <span className="text-xs text-zinc-500">Free Plan</span>
              )}
            </div>
          </div>
          <DropdownMenuSeparator className="bg-white/10" />
          {planId === 'free' && (
            <>
              <DropdownMenuItem
                onClick={() => navigate('/pricing')}
                className="cursor-pointer text-amber-400 focus:text-amber-300"
                data-testid="daw-upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade Plan
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
            </>
          )}
          <DropdownMenuItem
            onClick={() => navigate('/profile')}
            className="cursor-pointer"
            data-testid="daw-profile-btn"
          >
            <User className="w-4 h-4 mr-2" />
            My Profile
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigate('/dashboard')}
            className="cursor-pointer"
            data-testid="daw-dashboard-btn"
          >
            <Home className="w-4 h-4 mr-2" />
            Dashboard
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-white/10" />
          <DropdownMenuItem
            onClick={() => {
              logout();
              navigate('/');
            }}
            className="text-rose-400 focus:text-rose-300 cursor-pointer"
            data-testid="daw-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default UserMenu;
